import React, { useEffect, useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";

import Icon from "./icons";

import { useUserSelector } from "../store/selectors/userSelector";

import { useUserApi } from "../store/api/hooks/userApiHook";
import { useLookup, getRoleDisplayNameByRawName } from "../_common/hookFunction";
import { LOOKUP_VALUE } from "../_common/constant";

import Button from "./elements/button";
import FormattedDate from "./elements/FormattedDate";
import { useNavigate } from "react-router-dom";
import { URLS } from "../_config";

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  children?: any;

  className?: string;

  title?: string;

  back?: string;

  _ref?: any;

  onClose?: () => void;

  outsideClose?: boolean;

  isAfterOpenRender?: boolean;
}

const Profile: React.FC<Props> = ({
  children,

  className = "",

  title,

  back,

  _ref,

  onClose,

  outsideClose = true,

  isAfterOpenRender = false,
}) => {
  const { t } = useTranslation("common");
  const [isOpen, setIsOpen] = useState(false);
  const [punchStatus, setPunchStatus] = useState<any>(null);
  const [loadingPunch, setLoadingPunch] = useState(false);
  const navigate = useNavigate();
  const user: any = useUserSelector();
  const userAPI = useUserApi();
  const lookup = useLookup();

  const toggleDrawer = () => {
    if (onClose && isOpen) {
      onClose();
    }

    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (_ref) {
      _ref.current = {
        open: () => {
          setIsOpen(true);
        },

        close: () => {
          setIsOpen(false);
        },
      };
    }
  }, [_ref]);

  useEffect(() => {
    if (isOpen) {
      fetchPunchStatus();
    }
  }, [isOpen]);

  const logout = () => {
    userAPI.logout(
      {},

      (response: any) => {
        console.log("response =>", response);
      },

      (error: any) => {
        console.log("error =>", error);
      }
    );
  };

  const fetchPunchStatus = () => {
    setLoadingPunch(true);
    userAPI.getLastPunchStatus(
      {},
      (response: any) => {
        setLoadingPunch(false);
        if (response?.success && response?.data?.users?.length > 0) {
          setPunchStatus(response.data.users[0]);
        }
      },
      (error: any) => {
        setLoadingPunch(false);
        console.error("Error fetching punch status:", error);
      }
    );
  };

  const handleClockInOut = () => {
    const punchTypes = lookup.getPunchType();
    let nextPunchTypeId;

    if (!punchStatus) {
      // No previous punch status, so clock in
      const punchInType = punchTypes.find((type: any) => type.lookupId === LOOKUP_VALUE.PUNCH_TYPE_SHIFT_START);
      nextPunchTypeId = punchInType?.lookupId;
    } else {
      const lastPunchType = punchTypes.find((type: any) => type.lookupId === punchStatus.fkLookupValueID);

      // Check if last punch time is today
      const lastPunchTime = moment.utc(punchStatus.lastPunchTime).local();
      const isToday = lastPunchTime.isSame(moment(), 'day');

      if (isToday) {
        // If punched in today, punch out; if punched out today, punch in
        if (lastPunchType?.lookupId === LOOKUP_VALUE.PUNCH_TYPE_SHIFT_START) {
          // Find punch out type
          const punchOutType = punchTypes.find((type: any) => type.lookupId === LOOKUP_VALUE.PUNCH_TYPE_SHIFT_END);
          nextPunchTypeId = punchOutType?.lookupId;
        } else {
          // Find punch in type
          const punchInType = punchTypes.find((type: any) => type.lookupId === LOOKUP_VALUE.PUNCH_TYPE_SHIFT_START);
          nextPunchTypeId = punchInType?.lookupId;
        }
      } else {
        // If not today, always punch in
        const punchInType = punchTypes.find((type: any) => type.lookupId === LOOKUP_VALUE.PUNCH_TYPE_SHIFT_START);
        nextPunchTypeId = punchInType?.lookupId;
      }
    }

    if (!nextPunchTypeId) {
      console.error("Could not determine next punch type");
      return;
    }

    const payload = {
      punchDateTime: moment().utc().toISOString(),
      fkLookupValueID: btoa(nextPunchTypeId.toString())
    };
    
    setLoadingPunch(true);
    userAPI.clockIn(
      payload,
      (response: any) => {
        setLoadingPunch(false);
        if (response?.success) {
          // Refresh punch status after successful clock in/out
          fetchPunchStatus();
        } else {
          console.error("Clock in/out failed:", response.errorMessage);
        }
      },
      (error: any) => {
        setLoadingPunch(false);
        console.error("Error during clock in/out:", error);
      }
    );
  };

  const getPunchButtonText = () => {
    if (!punchStatus) return t("profile.shiftStart");

    const punchTypes = lookup.getPunchType();
    const lastPunchType = punchTypes.find((type: any) => type.lookupId === punchStatus.fkLookupValueID);

    // Check if last punch time is today
    const lastPunchTime = moment.utc(punchStatus.lastPunchTime).local();
    const isToday = lastPunchTime.isSame(moment(), 'day');

    if (isToday) {
      // If punched in today, show punch out; if punched out today, show punch in
      return lastPunchType?.lookupId === LOOKUP_VALUE.PUNCH_TYPE_SHIFT_START ? t("profile.shiftEnd") : t("profile.shiftStart");
    } else {
      // If not today, always show punch in
      return t("profile.shiftStart");
    }
  };

  const getPunchStatusText = () => {
    if (!punchStatus) return t("profile.shiftNotStarted");

    const punchTypes = lookup.getPunchType();
    const lastPunchType = punchTypes.find((type: any) => type.lookupId === punchStatus.fkLookupValueID);

    // Check if last punch time is today
    const lastPunchTime = moment.utc(punchStatus.lastPunchTime).local();
    const isToday = lastPunchTime.isSame(moment(), 'day');

    if (isToday) {
      return lastPunchType?.lookupId === LOOKUP_VALUE.PUNCH_TYPE_SHIFT_START ? t("profile.shiftStarted") : t("profile.shiftNotStarted");
    } else {
      return t("profile.shiftNotStarted");
    }
  };

  return (
    <div className="drawer drawer-end w-auto">
      <input
        type="checkbox"
        className="drawer-toggle hidden"
        checked={isOpen}
        onChange={toggleDrawer}
      />

      <button
        className="drawer-content flex items-center gap-4"
        onClick={toggleDrawer}
      >
        <img
          src={user?.profilePicture || "/default-profile-picture.jpg"}
          alt={t("profile.profileAlt")}
          className="w-10 h-10 rounded-full"
        />

        <span className="text-md font-medium">{user?.username}</span>
      </button>

      <div className="drawer-side fixed z-50">
        <label
          className="drawer-overlay"
          onClick={() => {
            if (outsideClose) {
              toggleDrawer();
            }
          }}
        ></label>

        <div
          className={`menu py-5 px-[15px] w-1/4 flex flex-col bg-white text-base-content h-screen `}
        >
          <div className="flex mx-auto w-11/12 items-center justify-between border-b border-slate-300 pb-[15px] mb-2 flex-shrink-0 ">
            <p className="text-[20px] leading-none text-slate-800 font-normal">
              {t("profile.title")}
            </p>

            <button
              onClick={toggleDrawer}
              className="bg-slate-800 flex items-center justify-center text-white w-[45px] h-[45px] rounded-full"
            >
              <Icon name="close" size={30} className="ml-[-2px]" />
            </button>
          </div>

          <div className="flex-1 mx-auto w-11/12 overflow-auto relative">
            <div className="bg-[#E6E3EE] border-b border-slate-400 p-[18px] mb-8 rounded-[2px] shadow-sm overflow-hidden">
              <p className="text-[18px] text-slate-900 font-semibold leading-[1.2]">
                {user?.username}
              </p>

              <p className="text-[14px] text-slate-900 font-semibold leading-[1.2]">
                {user?.roles?.map((role: any) => getRoleDisplayNameByRawName(role.roleName, t)).join(', ') || getRoleDisplayNameByRawName(user?.roleName, t)}
              </p>

              <p className="text-[11px] text-slate-900 leading-[1.9] mt-2">
                {atob(user?.email)}
              </p>
              <div className="flex justify-between items-center">
                <p
                  className="text-[11px] text-primary leading-[1.2] hover:underline cursor-pointer"
                  onClick={logout}
                >
                  {t("profile.signOut")}
                </p>
                <p
                  className="text-[11px] leading-[1.2] max-h-12 cursor-pointer px-3 py-1 text-xs text-secondary border border-secondary rounded transition-colors bg-neutral"
                  onClick={()=>{
                    navigate(URLS.RESET_PASSWORD);
                    toggleDrawer();
                  }}
                >
                  {t("profile.resetPassword")}
                </p>
              </div>
            </div>

            {/* Shift Start/Out Section */}

            <div className="flex flex-col gap-3 mb-8">
              <p className="text-[14px] text-slate-800 font-semibold mb-2">
                {loadingPunch ? t("profile.loadingStatus") : getPunchStatusText()}
              </p>

              {loadingPunch ? (
                <p className="text-[12px] text-slate-700 leading-[1.5]">
                  {t("profile.loadingPunchHistory")}
                </p>
              ) : (
                punchStatus?.lastPunchTime && (
                  <p className="text-[12px] text-slate-700 leading-[1.5]">
                    {t("profile.lastPunch")} <FormattedDate  date={punchStatus.lastPunchTime} format="dateTime"/>
                  </p>
                )
              )}

              <Button type="button" disabled={loadingPunch} onClick={handleClockInOut}>
                {loadingPunch ? t("profile.loadingEllipsis") : getPunchButtonText()}
              </Button>
            </div>

            {/* Sales Summary Section */}

            {/* <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between border-b pb-2">
                <div>
                  <p className="text-[13px] font-semibold text-slate-800">
                    Today
                  </p>

                  <p className="text-[11px] text-slate-600">0 Sales</p>
                </div>

                <p className="text-[13px] font-semibold text-slate-900">
                  ₹0.00
                </p>
              </div>
              <div className="flex items-center justify-between border-b pb-2">
                <div>
                  <p className="text-[13px] font-semibold text-slate-800">
                    This month
                  </p>

                  <p className="text-[11px] text-slate-600">0 Sales</p>
                </div>

                <p className="text-[13px] font-semibold text-slate-900">
                  ₹0.00
                </p>
              </div>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
